import { forwardRef, useState, memo, useEffect } from "react";
import { useField } from "formik";
import PropTypes from "prop-types";
import { Upload, Form, message, Button, Modal, Tooltip } from "antd";
import { CloseOutlined, PlusOutlined, InfoCircleOutlined } from "@ant-design/icons";
import _isEmpty from "lodash/isEmpty";
import _invoke from "lodash/invoke";
import Helper from "utils/helpers";
import "../style.less";

const ContestCandidateImageUpload = forwardRef(
  ({ 
    label, 
    className = "", 
    required, 
    formType = "candidate", // "candidate" or "contest"
    maxImages = 10,
    imageSize = "25MB",
    acceptedFormats = ["image/jpeg", "image/png"],
    helpText,
    ...props 
  }) => {
    const [field, meta, helper] = useField(props);
    const isError = meta.error && meta.touched;
    const [isLoading, setIsloading] = useState(false);
    const [values, setValues] = useState([]);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState("");

    useEffect(() => {
      if (!_isEmpty(values)) {
        const currentValues = field.value || [];
        const newValues = [...new Set(currentValues.concat(values))];
        helper.setValue(newValues);
        setValues([]);
        
        // Call handleChangeImages if provided
        _invoke(props, "handleChangeImages", {
          images: newValues.map((value) =>
            typeof value === "string" ? value : value.url
          ),
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    const image = !_isEmpty(field.value)
      ? (field.value || [])
          .filter((img) => typeof img === "string" || img.url)
          .map((img) =>
            typeof img === "string" ? { uid: img, url: img } : img
          )
      : [];

    const beforeUpload = (file) => {
      const isValidFormat = acceptedFormats.includes(file.type);
      if (!isValidFormat) {
        message.error(`Bạn chỉ có thể đăng ảnh ${acceptedFormats.map(f => f.split('/')[1].toUpperCase()).join(' hoặc ')}`);
        return false;
      }
      
      const maxSizeMB = parseInt(imageSize);
      const isLtMaxSize = file.size / 1024 / 1024 <= maxSizeMB;
      if (!isLtMaxSize) {
        message.error(`Ảnh phải nhỏ hơn ${imageSize}!`);
        return false;
      }

      // Check if we've reached the maximum number of images
      if (image.length >= maxImages) {
        message.error(`Bạn chỉ có thể đăng tối đa ${maxImages} ảnh!`);
        return false;
      }

      return true;
    };

    const handleRemove = (file) => {
      console.log("=== DEBUG: Image Removal Process ===");
      console.log("File to remove:", file);
      console.log("Current field.value:", field.value);
      console.log("File uid:", file.uid);
      console.log("File url:", file.url);
      
      if (!field.value || !Array.isArray(field.value)) {
        console.error("field.value is not an array:", field.value);
        return;
      }
      
      // Find the index of the image to remove based on position in the image array
      const currentImages = image;
      const fileIndex = currentImages.findIndex(img => {
        // Match by both uid and url for accuracy
        return img.uid === file.uid && img.url === file.url;
      });
      
      console.log("File index to remove:", fileIndex);
      console.log("Current images array:", currentImages);
      
      if (fileIndex === -1) {
        console.error("Could not find image to remove");
        return;
      }
      
      // Remove the image at the specific index from field.value
      const newValues = field.value.filter((img, index) => {
        // Keep all images except the one at the found index
        return index !== fileIndex;
      });
      
      console.log("Filtered newValues:", newValues);
      console.log("Images removed:", field.value.length - newValues.length);
      
      helper.setValue(newValues);
      
      const mappedImages = newValues.map((value) =>
        typeof value === "string" ? value : value.url
      );
      console.log("Mapped images for handleChangeImages:", mappedImages);
      
      _invoke(props, "handleChangeImages", {
        images: mappedImages,
      });
      
      console.log("=== END DEBUG ===");
    };

    const customRequest = async (option) => {
      const { file } = option;

      setIsloading(true);
      try {
        const img = await Helper.uploadFile({ file });
        if (img) {
          setValues((value) => value.concat(img.url));
        }
      } catch (error) {
        message.error("Có lỗi xảy ra khi tải ảnh lên. Vui lòng thử lại.");
      } finally {
        setIsloading(false);
      }
    };

    const handleCancel = () => setPreviewVisible(false);

    const handlePreview = async (file) => {
      setPreviewImage(file.url);
      setPreviewVisible(true);
    };

    const getFormTypeSpecificText = () => {
      if (formType === "contest") {
        return {
          uploadText: "Chọn ảnh cuộc thi",
          helpText: helpText || `Tải lên ảnh cho cuộc thi (tối đa ${maxImages} ảnh, mỗi ảnh < ${imageSize})`
        };
      }
      return {
        uploadText: "Chọn ảnh thí sinh",
        helpText: helpText || `Tải lên ảnh cho thí sinh (tối đa ${maxImages} ảnh, mỗi ảnh < ${imageSize})`
      };
    };

    const { uploadText, helpText: typeHelpText } = getFormTypeSpecificText();

    return (
      <div
        className={`input-container contest-candidate-upload-image-container ${className}`}
      >
        {label && (
          <div className="label-wrapper">
            <>{label}</>
            {required && <div className="required-mark">*</div>}
            {helpText && (
              <Tooltip title={typeHelpText}>
                <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
              </Tooltip>
            )}
          </div>
        )}
        <Form.Item
          validateStatus={isError ? "error" : ""}
          help={
            <div className="helper-wrapper">
              <div className="error-text">{isError && <>{meta.error}</>}</div>
              {!isError && typeHelpText && (
                <div className="help-text" style={{ fontSize: '12px', color: '#666' }}>
                  {typeHelpText}
                </div>
              )}
            </div>
          }
        >
          <Upload
            onPreview={handlePreview}
            listType="picture-card"
            fileList={image}
            showUploadList={{
              removeIcon: <CloseOutlined style={{ color: "#fff" }} />,
            }}
            beforeUpload={beforeUpload}
            onRemove={handleRemove}
            multiple
            customRequest={customRequest}
          >
            {image.length >= maxImages ? (
              <div style={{ textAlign: 'center', color: '#999' }}>
                <div>Đã đạt giới hạn</div>
                <div style={{ fontSize: '12px' }}>{maxImages} ảnh</div>
              </div>
            ) : (
              <Button disabled={isLoading} style={{ height: "100%" }}>
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>{uploadText}</div>
                  <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
                    {image.length}/{maxImages}
                  </div>
                </div>
              </Button>
            )}
          </Upload>
        </Form.Item>
        <Modal
          visible={previewVisible}
          title="Xem trước ảnh"
          footer={null}
          onCancel={handleCancel}
        >
          <img alt="preview-img" style={{ width: "100%" }} src={previewImage} />
        </Modal>
      </div>
    );
  }
);

ContestCandidateImageUpload.displayName = "ContestCandidateImageUpload";
ContestCandidateImageUpload.propTypes = {
  label: PropTypes.string,
  className: PropTypes.string,
  required: PropTypes.bool,
  formType: PropTypes.oneOf(["candidate", "contest"]),
  maxImages: PropTypes.number,
  imageSize: PropTypes.string,
  acceptedFormats: PropTypes.arrayOf(PropTypes.string),
  helpText: PropTypes.string,
};
export default memo(ContestCandidateImageUpload); 